// src/pages/billing/InvoiceDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingInvoice, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'INVOICE_STATUS_PAID': return 'success';
    case 'INVOICE_STATUS_PENDING': case 'INVOICE_STATUS_SENT': return 'primary';
    case 'INVOICE_STATUS_OVERDUE': return 'error';
    case 'INVOICE_STATUS_DRAFT': return 'secondary';
    case 'INVOICE_STATUS_PARTIALLY_PAID': return 'warning';
    case 'INVOICE_STATUS_VOIDED': case 'INVOICE_STATUS_UNCOLLECTIBLE': return 'error';
    default: return 'secondary';
  }
}

function statusLabel(status: string): string {
  return (status || '').replace('INVOICE_STATUS_', '').replace(/_/g, ' ');
}

export const InvoiceDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useBillingInvoice();
  const { t } = useI18n();
  const [actionLoading, setActionLoading] = useState('');
  const item = store.currentItem as any;

  useEffect(() => {
    if (id) store.GetInvoice(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleAction = async (action: string) => {
    if (!id) return;
    setActionLoading(action);
    try {
      if (action === 'finalize') await store.FinalizeInvoice(id, {});
      else if (action === 'void') await store.VoidInvoice(id, {});
      else if (action === 'pay') await store.PayInvoice(id, {});
      store.GetInvoice(id);
    } catch {
      // Error handled by store
    } finally {
      setActionLoading('');
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/billing/invoices" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/billing/invoices" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const isDraft = item.status === 'INVOICE_STATUS_DRAFT';
  const isPending = item.status === 'INVOICE_STATUS_PENDING' || item.status === 'INVOICE_STATUS_SENT' || item.status === 'INVOICE_STATUS_OVERDUE';

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/billing/invoices" className="text-muted-foreground hover:text-foreground">Invoices</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.number ?? id)}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">Invoice {String(item.number ?? '')}</h1>
            <Badge variant={statusVariant(item.status)} size="sm">{statusLabel(item.status)}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{String(item.customer_name ?? '')} &middot; {String(item.customer_email ?? '')}</p>
        </div>
        <div className="flex gap-2">
          {isDraft && (
            <>
              <Link to={`/billing/invoices/${id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
              <Button variant="primary" onClick={() => handleAction('finalize')} disabled={actionLoading === 'finalize'}>
                {actionLoading === 'finalize' ? 'Finalizing...' : 'Finalize'}
              </Button>
            </>
          )}
          {isPending && (
            <>
              <Button variant="primary" onClick={() => handleAction('pay')} disabled={actionLoading === 'pay'}>
                {actionLoading === 'pay' ? 'Processing...' : 'Record Payment'}
              </Button>
              <Button variant="danger" onClick={() => handleAction('void')} disabled={actionLoading === 'void'}>
                {actionLoading === 'void' ? 'Voiding...' : 'Void'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Amount summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Gross</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.amounts?.gross)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Net</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.amounts?.net)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Paid</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.amount_paid)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Remaining</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.amount_remaining)}</p></CardContent></Card>
      </div>

      {/* Invoice info */}
      <Card>
        <CardHeader><CardTitle>Invoice Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Invoice Date</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.invoice_date)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Due Date</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.due_date)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Period</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.period_start)} - {formatDate(item.period_end)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Memo</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.memo ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Billing Address</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.billing_address ? `${item.billing_address.line1 || ''}, ${item.billing_address.city || ''} ${item.billing_address.country || ''}` : '--'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Line items */}
      <Card>
        <CardHeader><CardTitle>Line Items</CardTitle></CardHeader>
        <CardContent>
          <Table striped>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Unit Price</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Tax</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(item.lines?.lines ?? []).map((line: any, i: number) => (
                <TableRow key={line.id || i}>
                  <TableCell>{String(line.description ?? '')}</TableCell>
                  <TableCell>{String(line.quantity ?? '')} {String(line.unit ?? '')}</TableCell>
                  <TableCell>{formatMoney(line.unit_price)}</TableCell>
                  <TableCell>{formatMoney(line.discount)}</TableCell>
                  <TableCell>{formatMoney(line.tax)}</TableCell>
                  <TableCell className="text-right font-medium">{formatMoney(line.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Taxes */}
      {item.taxes?.taxes?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Taxes</CardTitle></CardHeader>
          <CardContent>
            <Table striped>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Jurisdiction</TableHead>
                  <TableHead>Taxable Amount</TableHead>
                  <TableHead className="text-right">Tax Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {item.taxes.taxes.map((tax: any, i: number) => (
                  <TableRow key={tax.id || i}>
                    <TableCell>{String(tax.name ?? '')}</TableCell>
                    <TableCell>{String(tax.rate ?? '')}%</TableCell>
                    <TableCell>{String(tax.jurisdiction ?? '')}</TableCell>
                    <TableCell>{formatMoney(tax.taxable_amount)}</TableCell>
                    <TableCell className="text-right">{formatMoney(tax.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default InvoiceDetailPage;
